'use client';

import { Button, ButtonGroup, Text, Tooltip } from '@/_components';
import { useContext, useMemo, useState, type FC } from 'react';
import { PREDEFINED_NOT_APPLICABLE } from '@/_constants/questions';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { NotesButton } from '../NotesButton';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { QuestionAnswerLaterDialog } from './components/QuestionAnswerLaterDialog';
import type { QuestionActionsProps } from './QuestionActions.types';
import { getIsPastDate } from '@/_utils/dates';
import { Questionnaire } from '@/_types/models/questionnaire';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

export const QuestionActions: FC<QuestionActionsProps> = ({
  question,
  isDisabled,
  mode,
}) => {
  const { updateQuestion, selectedQuestionnaire } = useContext(
    ClientQuestionnaireContext,
  );
  const { updatePreviewQuestion, previewQuestionnaire } =
    useContext(PreviewContext);
  const { previewTemplate, updateTemplateQuestion } =
    useContext(UserPreviewContext);

  const [isQuestionAnswerLaterDialogOpen, setIsQuestionAnswerLaterDialogOpen] =
    useState(false);

  const questionnaire: Questionnaire | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestionnaire;
    if (mode === 'admin-template') return previewTemplate as any;

    return selectedQuestionnaire;
  }, [mode, selectedQuestionnaire, previewQuestionnaire, previewTemplate]);

  const isPastDue: boolean = getIsPastDate(questionnaire?.due_date);

  const isAnswerLater: boolean = !!question?.answer_by;
  const isNotApplicable: boolean =
    question?.answer === PREDEFINED_NOT_APPLICABLE;

  const showAnswerLaterButton: boolean = Boolean(
    !isAnswerLater &&
      !question?.disable_answer_later &&
      !question?.answer_by &&
      question.answer !== PREDEFINED_NOT_APPLICABLE,
  );

  const showNotApplicableButton: boolean = Boolean(
    !isNotApplicable && !question?.disable_answer_later && isAnswerLater,
  );

  const onNotApplicableToggle = async () => {
    const answerParams = {
      questionId: question.id,
      answer: isNotApplicable ? '' : PREDEFINED_NOT_APPLICABLE,
    };

    if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
    if (mode === 'admin-template') return updateTemplateQuestion(answerParams);

    return updateQuestion(answerParams);
  };

  const onAnswerNow = async () => {
    const answerParams = {
      questionId: question?.id!,
      answer: '',
      answerBy: '',
    };

    if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
    if (mode === 'admin-template') return updateTemplateQuestion(answerParams);

    return updateQuestion(answerParams);
  };

  const closeAnswerLaterDialog = () =>
    setIsQuestionAnswerLaterDialogOpen(false);
  const openAnswerLaterDialog = () => setIsQuestionAnswerLaterDialogOpen(true);

  const isAnswerLaterDisabled: boolean = isDisabled || isPastDue;

  const getAnswerLaterTooltipText = (): string => {
    if (isPastDue) return 'Questionnaire is past the due date';
    if (isDisabled) return 'Questionnaire is locked';

    return '';
  };

  const answerLaterTooltipText = getAnswerLaterTooltipText();

  return (
    <>
      <QuestionAnswerLaterDialog
        isAnswerLater={isAnswerLater}
        isOpen={isQuestionAnswerLaterDialogOpen}
        mode={mode}
        onCloseDialog={closeAnswerLaterDialog}
        question={question}
      />
      <ButtonGroup>
        {!question?.disable_not_applicable && (
          <Button
            iconLeft={isNotApplicable ? undefined : 'prohibit'}
            size="small"
            intent={isNotApplicable ? 'primary' : 'secondary'}
            value={isNotApplicable ? 'Answer Question' : 'Mark N/A'}
            onClick={onNotApplicableToggle}
            isDisabled={isDisabled}
          />
        )}
        {showAnswerLaterButton && (
          <Tooltip isDisabled={!isAnswerLaterDisabled}>
            <Tooltip.Content>
              <Text type="small" color="currentcolor">
                {answerLaterTooltipText}
              </Text>
            </Tooltip.Content>
            <Tooltip.Trigger>
              <Button
                size="small"
                intent="secondary"
                onClick={openAnswerLaterDialog}
                value="Answer Later"
                isActive={isAnswerLater}
                isDisabled={isAnswerLaterDisabled}
              />
            </Tooltip.Trigger>
          </Tooltip>
        )}
        {showNotApplicableButton && (
          <Button
            size="small"
            intent="primary"
            onClick={onAnswerNow}
            value="Answer Now"
            isDisabled={isDisabled}
          />
        )}
        <NotesButton question={question} mode={mode} isDisabled={isDisabled} />
      </ButtonGroup>
    </>
  );
};
